import { VideoStartEvent, VideoStateChangeEvent } from '../../types'
import { VideoStateChangeTrigger } from '@simplisafe/blueprint-web-dsl'
import { BaseVideoPlayer } from './BaseVideoPlayer'
import { ReplayerCurrentTime } from '../ReplayerProxy'

/**
 * This defines how closely synced the embedded videos have to be to the replayer to be considered "at" that time.
 * It is unfortunate that we can't have them be perfectly in sync but because the replayer is synchronous and can't wait
 * for the video.play() promises to resolve, we have to allow for some slop. If we set this too low, then we end up
 * constantly bouncing between buffering and playing states as the players will often very briefly go to a wait state for a
 * fraction of a second when they first play and there is now way for the replayer to await the play call to avoid it.
 */
export const ACCEPTABLE_SLOP_SECONDS = 0.05
export const ACCEPTABLE_SLOP_MILLIS = ACCEPTABLE_SLOP_SECONDS * 1000

/**
 * returns the timestamp of the most recent play event recorded for a video for a given moment in time. If the replayerCurrentTime is before a video has started, it returns 0
 * @param video
 * @param replayerCurrentTime
 */
export function getMostRecentPlayTime(video: BaseVideoPlayer, replayerCurrentTime: ReplayerCurrentTime) {
  if (video.getVideoStartEvent().startTimeEpoch <= replayerCurrentTime.epoch) {
    const playEventsFilter = (e: VideoStateChangeEvent) => e.trigger === VideoStateChangeTrigger.Play
    const mostRecentPlayEvent = getMostRecentVideoStateEvent(video.getVideoStartEvent(), video.getVideoStateChangeEvents(), replayerCurrentTime, {
      filter: playEventsFilter
    })
    if ((mostRecentPlayEvent as VideoStateChangeEvent).trigger === VideoStateChangeTrigger.Play) {
      return (mostRecentPlayEvent as VideoStateChangeEvent).eventTime
    } else {
      return (mostRecentPlayEvent as VideoStartEvent).startTimeEpoch
    }
  } else {
    return 0
  }
}

/**
 * returns the most recent event recorded for a video for a given moment in time. If the replayerCurrentTime is before a video has started, it returns nothing
 * @param videoStartEvent
 * @param videoStateChangeEvents
 * @param replayerCurrentTime
 * @param options
 */
export function getMostRecentVideoStateEvent(
  videoStartEvent: VideoStartEvent,
  videoStateChangeEvents: VideoStateChangeEvent[] | undefined,
  replayerCurrentTime: ReplayerCurrentTime,
  options?: { filter?: (e: VideoStateChangeEvent) => boolean }
): VideoStateChangeEvent | VideoStartEvent | undefined {
  // only return something if the replayerCurrentTime after the video start time
  if (videoStartEvent.startTimeEpoch <= replayerCurrentTime.epoch) {
    let latestChangeEvent: VideoStateChangeEvent | undefined
    if (videoStateChangeEvents) {
      const stateChangeEvents = options?.filter ? videoStateChangeEvents.filter(options.filter) : videoStateChangeEvents
      let latestEventTime: number | undefined
      for (const event of stateChangeEvents) {
        // if the video state change event was emitted before the replayerCurrentTime
        if (event.eventTime <= replayerCurrentTime.epoch && (!latestEventTime || latestEventTime < event.eventTime)) {
          latestEventTime = event.eventTime
          latestChangeEvent = event
        }
      }
    }

    if (latestChangeEvent) {
      return latestChangeEvent
    } else {
      return videoStartEvent
    }
  }
}

export function sortVideosByMostRecentPlayEvent(videos: BaseVideoPlayer[], replayerCurrentTime: ReplayerCurrentTime) {
  return videos.sort((a, b) => {
    const startA = getMostRecentPlayTime(a, replayerCurrentTime)
    const startB = getMostRecentPlayTime(b, replayerCurrentTime)
    return startA - startB
  })
}

export function isElementVisible(el: HTMLElement): boolean {
  if (!el) return false

  const style = getComputedStyle(el)

  return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null
}
